import React, { useState } from 'react';
import { Factory } from 'lucide-react';
import type { Supplier } from '../types';
import { resolveSupplierImage } from '../utils/resolveSupplierImage';

// O painel grava a mesma foto como logo_<n>.webp (image) e thumb_<n>.webp (imageThumb).
function uploadId(url: string | undefined): string | null {
  if (!url) return null;
  let path = url;
  try {
    path = decodeURIComponent(url);
  } catch {
    /* mantém a URL crua */
  }
  return path.match(/\/(?:logo|thumb)_(\d+)\.\w+/)?.[1] ?? null;
}

interface SupplierCardProps {
  s: Supplier;
  isOpen: boolean;
  onSelect: (id: string) => void;
}

export default function SupplierCard({ s, isOpen, onSelect }: SupplierCardProps) {
  const [bannerFailed, setBannerFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const nome = s.tradeName || s.companyName || 'Fornecedor';
  const inicial = (s.tradeName || s.companyName || 'F').trim().charAt(0).toUpperCase() || 'F';

  const banner = resolveSupplierImage(s, 'detail');
  // Mesma validação de URL do resolveSupplierImage, aplicada só ao thumb.
  const thumb = resolveSupplierImage({ imageThumb: s.imageThumb }, 'card');
  const image = (s.image || '').trim();
  const sameUpload = uploadId(thumb) !== null && uploadId(thumb) === uploadId(image);
  const logo = thumb && thumb !== image && !sameUpload ? thumb : '';

  const showBanner = Boolean(banner) && !bannerFailed;
  const showLogo = Boolean(logo) && !logoFailed;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(s.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect(s.id);
      }}
      className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer group hover:shadow-lg hover:border-emerald-200 hover:-translate-y-0.5 transition-all duration-300 touch-manipulation"
    >
      <div className="relative h-[140px]">
        <div className="absolute inset-0 overflow-hidden">
          {showBanner ? (
            <img
              src={banner}
              alt={nome}
              loading="lazy"
              onError={() => setBannerFailed(true)}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div className="relative h-full w-full bg-gradient-to-br from-slate-800 to-slate-900">
              <Factory size={16} strokeWidth={2} className="absolute bottom-2 right-2 text-white/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>

        <span className="absolute top-2 left-2 bg-slate-900/80 text-white text-[9px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">
          ATACADO
        </span>

        <span
          className={`absolute top-2 right-2 flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
            isOpen ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
          {isOpen ? 'Aberto' : 'Loja fechada'}
        </span>

        <div className="absolute -bottom-5 left-3 z-10 w-11 h-11 rounded-full ring-2 ring-white shadow-md bg-slate-800 overflow-hidden flex items-center justify-center">
          {showLogo ? (
            <img
              src={logo}
              alt=""
              loading="lazy"
              onError={() => setLogoFailed(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-base font-black text-white">{inicial}</span>
          )}
        </div>
      </div>

      <div className="px-3 pt-6 pb-2.5">
        <h4 className="text-[14px] font-extrabold text-slate-800 leading-tight line-clamp-1">{nome}</h4>
        <p className="text-[10px] text-slate-500 line-clamp-1">{s.description || 'Atacado & Distribuição'}</p>
        <div className="flex justify-between items-center border-t border-slate-100 pt-1.5 mt-2">
          {s.minimumOrder != null && s.minimumOrder > 0 ? (
            <span className="text-[10px] text-slate-500 whitespace-nowrap">Mín. R$ {Number(s.minimumOrder).toFixed(0)}</span>
          ) : (
            <span />
          )}
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
            Ver catálogo →
          </span>
        </div>
      </div>
    </div>
  );
}
